import { useState, useEffect, FC } from "react";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { get, size, omit } from "lodash";
import { useCreateDashboardStore, ColorPicker } from "..";
import DashboardCreateSelectDeviceModal from "../modal/SelectDevices";

import { COLORS } from "constant";
import { MultipleChartColumnProps } from "types/Charts";
import { getSensorTypes } from "utils/gatewayFunctions";
import { SensorTypeObject } from "types";
import { assignUniqueColorsToDevices } from "utils/chart";
import CloseOutlined from "@mui/icons-material/CloseOutlined";

const maxDevicesAllowed = 8;

interface CorrelationChartColumnProps extends MultipleChartColumnProps {
    columnType: "sensor" | "actuator";
}

export const CorrelationChartColumn: FC<CorrelationChartColumnProps> = (
    props
) => {
    const { column, onChange, data, columnType } = props;

    const isSensor = columnType === "sensor";
    const displayName = isSensor ? "sensor" : "actuator";

    const [showSelectModal, setShowSelectModal] = useState(false);
    const [selectedDevices, setSelectedDevices] = useState<any>({});

    const [maxDevices, setMaxDevices] = useState<number>(maxDevicesAllowed);
    const [deviceTypeSelector, setDeviceTypeSelector] = useState<
        SensorTypeObject[]
    >([]);

    const [selectedDeviceType, setSelectedDeviceType] = useState<string>(
        get(data, `data[${column}].sensor_type`, "") ||
        get(deviceTypeSelector, `[0].id`, "")
    );
    const [eventTrigger, setEventTrigger] = useState<any>();
    const { deviceList } = useCreateDashboardStore();

    useEffect(() => {
        const filterFn = isSensor
            ? (d: any) => !d.isActuator
            : (d: any) => d.isActuator;

        !deviceTypeSelector.length &&
            setDeviceTypeSelector(getSensorTypes(deviceList.filter(filterFn)));

        // Calculate max devices based on other column
        const otherColumn = column === 0 ? 1 : 0;
        const otherDevicesCount = size(
            get(data, `data.${otherColumn}.devices`, {})
        );

        setMaxDevices(
            maxDevicesAllowed - otherDevicesCount
        );

        setSelectedDevices(data.data[column]?.devices || {});

        setSelectedDeviceType(
            get(data, `data[${column}].sensor_type`, "") ||
            get(deviceTypeSelector, `[0].id`, "")
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, deviceList, deviceTypeSelector]);

    useEffect(() => {
        if (eventTrigger) {
            onChange({
                devices: selectedDevices,
                sensor_type: selectedDeviceType,
            });
            setEventTrigger(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDevices, eventTrigger]);

    const onRemove = (event: any, key: string) => {
        setSelectedDevices(omit(selectedDevices, key));
        setEventTrigger(event);
    };

    const onDevicesSelected = (event: any, devicesData: any) => {
        assignUniqueColorsToDevices(
            devicesData,
            [data.data[0], data.data[1]],
            COLORS
        );
        setSelectedDevices(devicesData);
        setEventTrigger(event);
    };
    return (
        <Col lg={12} xl={6}>
            <div className="multiple-chart-block__panel rounded p-3">
                <p>Select {displayName} type</p>

                <Form.Control
                    as="select"
                    custom
                    value={selectedDeviceType}
                    onChange={(event) => {
                        setSelectedDevices({});
                        setSelectedDeviceType(event.target.value);
                        setEventTrigger(event);
                    }}
                    aria-label={`Select ${displayName} type`}
                >
                    {deviceTypeSelector.map(({ id, name }) => (
                        <option key={id} value={id}>
                            {name}
                        </option>
                    ))}
                </Form.Control>

                <div className="mt-4 d-flex gap-2 flex-wrap flex-column flex-lg-row">
                    <Button
                        variant="primary"
                        onClick={() => setShowSelectModal(true)}
                        disabled={!selectedDeviceType}
                    >
                        {isSensor ? "Select Sensors" : "Select Actuators"}
                    </Button>
                    <div className="text-primary-blue-4 align-self-center font-size-13px">
                        Select {displayName}s of the chosen {displayName} type.
                    </div>
                </div>

                <div className="cstm-table mt-4">
                    {size(selectedDevices) !== 0 && (
                        <div className="table-head" key="head">
                            <Row className="no-checkbox">
                                <Col md={4}>
                                    {isSensor ? "Sensor" : "Actuators"}
                                </Col>
                                <Col md={3}>Type</Col>
                                <Col md={3}>Color</Col>
                                <Col md={2}></Col>
                            </Row>
                        </div>
                    )}

                    {Object.keys(selectedDevices).map((key) => {
                        const device = selectedDevices[key];
                        return (
                            <div key={key} className="table-row">
                                <Row className="flex-column flex-md-row">
                                    <Col
                                        md={4}
                                        className="no-checkbox d-flex align-items-center flex-row"
                                    >
                                        <Col className="d-block d-md-none multiple-chart-label-wrapper">
                                            <p>
                                                {isSensor
                                                    ? "Sensor"
                                                    : "Actuators"}
                                            </p>
                                        </Col>
                                        <Col>
                                            <p className="text-break">
                                                {device.sensorName}
                                            </p>
                                        </Col>
                                    </Col>
                                    <Col
                                        md={3}
                                        className="no-checkbox d-flex align-items-center flex-row"
                                    >
                                        <Col className="d-block d-md-none multiple-chart-label-wrapper">
                                            <p>Type</p>
                                        </Col>
                                        <Col>
                                            <p>{device.sensorType}</p>
                                        </Col>
                                    </Col>
                                    <Col
                                        md={3}
                                        className="no-checkbox d-flex align-items-center flex-row"
                                    >
                                        <Col className="d-block d-md-none multiple-chart-label-wrapper">
                                            <p>Color</p>
                                        </Col>
                                        <Col>
                                            <ColorPicker
                                                defaultValue={device.color}
                                                onChange={(_, color) => {
                                                    setSelectedDevices({
                                                        ...selectedDevices,
                                                        [device.deviceId]: {
                                                            ...device,
                                                            color,
                                                        },
                                                    });
                                                    setEventTrigger("color");
                                                }}
                                            />
                                        </Col>
                                    </Col>
                                    <Col
                                        md={2}
                                        className="no-checkbox d-flex align-items-center justify-content-center"
                                    >
                                        <CloseOutlined
                                            onClick={(event) =>
                                                onRemove(event, device.deviceId)
                                            }
                                            className="cursor-pointer text-primary-red-1"
                                        />
                                    </Col>
                                </Row>
                            </div>
                        );
                    })}
                </div>
            </div>

            <DashboardCreateSelectDeviceModal
                show={showSelectModal}
                onHide={() => setShowSelectModal(false)}
                values={selectedDevices}
                onSave={onDevicesSelected}
                deviceType={isSensor ? "Sensor" : "Actuator"}
                sensorTypes={
                    selectedDeviceType ? [selectedDeviceType] : undefined
                }
                maxDevices={maxDevices}
            />
        </Col>
    );
};
